import {
  addDynamicItemBelow,
  addPricingItemBelow,
  DynamicItem,
  moveDynamicItem,
  movePricingItem,
  newDynamicItem,
  newPricingItem,
  Pricing,
  PricingItem,
  PricingItemKind,
  PricingPatch,
  removeDynamicItem,
  removePricingItem,
  updateDynamicItem,
  updatePricingItem,
} from "../types/pricing";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellCheckbox,
  ETableCellDecimalInput,
  ETableCellInput,
  ETableCellSelect,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";
import {
  DragNDropWrapper,
  ETableCellDragNDropGrip,
} from "@/shared/components/tables/e-table-cell-drag-n-drop";
import { DragEndEvent } from "@dnd-kit/core";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Button } from "@/shared/components/ui/button";
import { EllipsisVertical, Plus, Trash2 } from "lucide-react";
import { Switch } from "@/shared/components/ui/switch";
import { Label } from "@/shared/components/ui/label";
import { Field } from "@/shared/components/ui/field";
import {
  PriceModifierKind,
  priceModifierAmount,
  PriceModifierSchema,
  withPriceModifierAmount,
} from "../../pricemodifier/types/price-modifier";
import { cn } from "cn";
import { PositionKind } from "../../position/types/position-kind";
import { PositionUnitSelectItems } from "../../position/types/position-unit";

export type PricingComponentProps = {
  pricing: Pricing;
  onPatch: (patch: PricingPatch) => void;
  title?: string;
  disabled?: boolean;
  /** Offers to derive the pricing from the positions. */
  usePositionsSwitch?: boolean;
  /**
   * Offers to activate the pricing. Without it, an inactive pricing
   * renders nothing.
   */
  activeSwitch?: boolean;
};

/** PricingCardConfig are the props of a {@link PricingCard} a service kind sets. */
export type PricingCardConfig = Pick<
  PricingComponentProps,
  "usePositionsSwitch" | "activeSwitch"
>;

export function PricingCard({
  pricing,
  onPatch,
  title,
  disabled,
  usePositionsSwitch = true,
  activeSwitch = true,
}: PricingComponentProps) {
  if (!activeSwitch && !pricing.active) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title || t("Preise")}</CardTitle>
        <CardAction className="flex items-center gap-4">
          {pricing.active && usePositionsSwitch && (
            <Field orientation="horizontal">
              <Switch
                id="pricing-use-positions"
                checked={pricing.usePositions}
                onCheckedChange={(usePositions) => onPatch({ usePositions })}
                disabled={disabled}
              />
              <Label htmlFor="pricing-use-positions">
                {t("Daten aus Positionen")}
              </Label>
            </Field>
          )}
          {activeSwitch && (
            <Field orientation="horizontal">
              <Switch
                id="pricing-active"
                checked={pricing.active}
                onCheckedChange={(active) => onPatch({ active })}
                disabled={disabled}
              />
              <Label htmlFor="pricing-active">{t("Aktiv")}</Label>
            </Field>
          )}
        </CardAction>
      </CardHeader>
      {pricing.active && (
        <CardContent>
          {/* the backend derives the items from the positions and rejects a
            manual patch of them */}
          <PricingItems
            pricing={pricing}
            onPatch={onPatch}
            disabled={disabled}
            readOnly={pricing.usePositions}
          />
          <h3 className="mt-3 mb-2 text-sm font-medium">
            {t("Zusatzleistungen")}
          </h3>
          <DynamicPricingItems
            pricing={pricing}
            onPatch={onPatch}
            disabled={disabled}
            readOnly={pricing.usePositions}
          />
        </CardContent>
      )}
    </Card>
  );
}

function PricingItems({
  pricing,
  onPatch,
  disabled,
  readOnly,
}: {
  pricing: Pricing;
  onPatch: (p: PricingPatch) => void;
  disabled?: boolean;
  readOnly?: boolean;
}) {
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const items = movePricingItem(
      pricing.items,
      Number(active.id),
      Number(over.id),
    );
    if (items !== pricing.items) {
      onPatch({ items });
    }
  };

  return (
    <DragNDropWrapper
      items={pricing.items.map((i) => i.order)}
      handleDragEnd={handleDragEnd}
      disabled={disabled || readOnly}
    >
      <ETable>
        <ETableHeader>
          <ETableRow>
            <ETableHead />
            {/* Name covers the active, amount and %/€ columns of
                modifiers too; their borders are hidden to read as one */}
            <ETableHead className="w-full border-r-0">{t("Name")}</ETableHead>
            <ETableHead aria-hidden="true" className="border-x-0" />
            <ETableHead aria-hidden="true" className="border-x-0" />
            <ETableHead aria-hidden="true" className="border-l-0" />
            <ETableHead>{t("Preis")}</ETableHead>
            <ETableHead />
          </ETableRow>
        </ETableHeader>
        <ETableBody>
          {pricing.items.length === 0 && (
            // without items, the actions menu adds the first one
            <ETableRow>
              <ETableCell aria-hidden="true" colSpan={6} />
              <PricingItemActionsCell
                pricing={pricing}
                onPatch={onPatch}
                disabled={disabled || readOnly}
              />
            </ETableRow>
          )}
          {pricing.items.map((item) => (
            <PricingItemRow
              key={item.order}
              item={item}
              pricing={pricing}
              onPatch={onPatch}
              disabled={disabled}
              readOnly={readOnly}
            />
          ))}
        </ETableBody>
      </ETable>
    </DragNDropWrapper>
  );
}

function PricingItemRow({
  item,
  pricing,
  onPatch,
  disabled,
  readOnly,
}: {
  item: PricingItem;
  pricing: Pricing;
  onPatch: (p: PricingPatch) => void;
  disabled?: boolean;
  readOnly?: boolean;
}) {
  switch (item.kind) {
    case PricingItemKind.Modifier:
      if (item.priceModifier.schema === PriceModifierSchema.Relative) {
        return (
          <ETableRow>
            <ETableCellDragNDropGrip
              id={item.order}
              disabled={disabled || readOnly}
            />
            <ETableCellInput
              value={item.text}
              // a modifier carries its text as title as well
              onChange={(e) =>
                onPatch({
                  items: updatePricingItem(pricing.items, item.order, {
                    ...item,
                    text: e.target.value,
                    priceModifier: {
                      ...item.priceModifier,
                      title: e.target.value,
                    },
                  }),
                })
              }
              className={cn("w-full", item.parentIdx !== 0 && "pl-6")}
              fieldName={`items.${item.order}.text`}
              readOnly={readOnly}
              disabled={disabled}
            />
            <ETableCellCheckbox
              label={t("Aktiv")}
              checked={item.priceModifier.active}
              onCheckedChange={(active) =>
                onPatch({
                  items: updatePricingItem(pricing.items, item.order, {
                    ...item,
                    priceModifier: {
                      ...item.priceModifier,
                      active: active === true,
                    },
                  }),
                })
              }
              fieldName={`items.${item.order}.priceModifier.active`}
              disabled={disabled || readOnly}
            />
            {/* the rate in percent, entered unsigned: the sign follows discount or surcharge */}
            <ETableCellDecimalInput
              value={priceModifierAmount(item.priceModifier)}
              onValueChange={(amount) =>
                onPatch({
                  items: updatePricingItem(pricing.items, item.order, {
                    ...item,
                    priceModifier: withPriceModifierAmount(
                      item.priceModifier,
                      amount,
                    ),
                  }),
                })
              }
              precision={2}
              fieldName={`items.${item.order}.priceModifier.relativeRate`}
              readOnly={readOnly}
              disabled={disabled}
            />
            <ETableCell className="w-8 text-center">%</ETableCell>
            <ETableCellDecimalInput
              value={item.price}
              onValueChange={() => null}
              precision={2}
              readOnly
            />
            <PricingItemActionsCell
              item={item}
              pricing={pricing}
              onPatch={onPatch}
              disabled={disabled || readOnly}
            />
          </ETableRow>
        );
      }
      return (
        <ETableRow>
          <ETableCellDragNDropGrip
            id={item.order}
            disabled={disabled || readOnly}
          />
          <ETableCellInput
            value={item.text}
            // a modifier carries its text as title as well
            onChange={(e) =>
              onPatch({
                items: updatePricingItem(pricing.items, item.order, {
                  ...item,
                  text: e.target.value,
                  priceModifier: {
                    ...item.priceModifier,
                    title: e.target.value,
                  },
                }),
              })
            }
            className={cn("w-full", item.parentIdx !== 0 && "pl-6")}
            fieldName={`items.${item.order}.text`}
            readOnly={readOnly}
            disabled={disabled}
          />
          <ETableCellCheckbox
            label={t("Aktiv")}
            checked={item.priceModifier.active}
            onCheckedChange={(active) =>
              onPatch({
                items: updatePricingItem(pricing.items, item.order, {
                  ...item,
                  priceModifier: {
                    ...item.priceModifier,
                    active: active === true,
                  },
                }),
              })
            }
            fieldName={`items.${item.order}.priceModifier.active`}
            disabled={disabled || readOnly}
          />
          {/* the value in euros, entered unsigned: the sign follows discount or surcharge */}
          <ETableCellDecimalInput
            value={priceModifierAmount(item.priceModifier)}
            onValueChange={(amount) =>
              onPatch({
                items: updatePricingItem(pricing.items, item.order, {
                  ...item,
                  priceModifier: withPriceModifierAmount(
                    item.priceModifier,
                    amount,
                  ),
                }),
              })
            }
            precision={2}
            fieldName={`items.${item.order}.priceModifier.value`}
            readOnly={readOnly}
            disabled={disabled}
          />
          <ETableCell className="w-8 text-center">€</ETableCell>
          <ETableCellDecimalInput
            value={item.price}
            onValueChange={() => null}
            precision={2}
            readOnly
          />
          <PricingItemActionsCell
            item={item}
            pricing={pricing}
            onPatch={onPatch}
            disabled={disabled || readOnly}
          />
        </ETableRow>
      );
    case PricingItemKind.SubTotal:
      return (
        <ETableRow className="font-semibold">
          <ETableCellDragNDropGrip
            id={item.order}
            disabled={disabled || readOnly}
          />
          <ETableCellInput
            value={item.text}
            onChange={(e) =>
              onPatch({
                items: updatePricingItem(pricing.items, item.order, {
                  ...item,
                  text: e.target.value,
                }),
              })
            }
            className={cn("w-full", item.parentIdx !== 0 && "pl-6")}
            fieldName={`items.${item.order}.text`}
            colSpan={4}
            readOnly={readOnly}
            disabled={disabled}
          />
          <ETableCellDecimalInput
            value={item.price}
            onValueChange={() => null}
            precision={2}
            readOnly
          />
          <PricingItemActionsCell
            item={item}
            pricing={pricing}
            onPatch={onPatch}
            disabled={disabled || readOnly}
          />
        </ETableRow>
      );
    case PricingItemKind.Total:
      return (
        <ETableRow className="font-bold">
          <ETableCell />
          <ETableCellInput
            value={item.text}
            onChange={(e) =>
              onPatch({
                items: updatePricingItem(pricing.items, item.order, {
                  ...item,
                  text: e.target.value,
                }),
              })
            }
            fieldName={`items.${item.order}.text`}
            colSpan={4}
            readOnly={readOnly}
            disabled={disabled}
          />
          <ETableCellDecimalInput
            value={item.price}
            onValueChange={() => null}
            precision={2}
            readOnly
          />
          <PricingItemActionsCell
            item={item}
            pricing={pricing}
            onPatch={onPatch}
            disabled={disabled || readOnly}
          />
        </ETableRow>
      );
    case PricingItemKind.Text:
      return (
        <ETableRow>
          <ETableCellDragNDropGrip
            id={item.order}
            disabled={disabled || readOnly}
          />
          <ETableCellInput
            value={item.text}
            onChange={(e) =>
              onPatch({
                items: updatePricingItem(pricing.items, item.order, {
                  ...item,
                  text: e.target.value,
                }),
              })
            }
            className={cn(item.parentIdx !== 0 && "pl-6")}
            fieldName={`items.${item.order}.text`}
            colSpan={4}
            readOnly={readOnly}
            disabled={disabled}
          />
          <PricingItemActionsCell
            item={item}
            pricing={pricing}
            onPatch={onPatch}
            disabled={disabled || readOnly}
          />
        </ETableRow>
      );
    default:
      return (
        <ETableRow>
          <ETableCellDragNDropGrip
            id={item.order}
            disabled={disabled || readOnly}
          />
          <ETableCellInput
            value={item.text}
            onChange={(e) =>
              onPatch({
                items: updatePricingItem(pricing.items, item.order, {
                  ...item,
                  text: e.target.value,
                }),
              })
            }
            className={cn(item.parentIdx !== 0 && "pl-6")}
            fieldName={`items.${item.order}.text`}
            colSpan={4}
            readOnly={readOnly}
            disabled={disabled}
          />
          <ETableCellDecimalInput
            value={item.price}
            onValueChange={(price) =>
              onPatch({
                items: updatePricingItem(pricing.items, item.order, {
                  ...item,
                  price,
                }),
              })
            }
            precision={2}
            fieldName={`items.${item.order}.price`}
            readOnly={readOnly}
            disabled={disabled}
          />
          <PricingItemActionsCell
            item={item}
            pricing={pricing}
            onPatch={onPatch}
            disabled={disabled || readOnly}
          />
        </ETableRow>
      );
  }
}

const addItemOptions = [
  { label: t("Posten"), item: () => newPricingItem(PricingItemKind.Default) },
  { label: t("Text"), item: () => newPricingItem(PricingItemKind.Text) },
  {
    label: t("Zwischensumme"),
    item: () => newPricingItem(PricingItemKind.SubTotal),
  },
  {
    label: t("Rabatt (%)"),
    item: () =>
      newPricingItem(PricingItemKind.Modifier, {
        kind: PriceModifierKind.Discount,
        schema: PriceModifierSchema.Relative,
      }),
  },
  {
    label: t("Rabatt (€)"),
    item: () =>
      newPricingItem(PricingItemKind.Modifier, {
        kind: PriceModifierKind.Discount,
        schema: PriceModifierSchema.Absolute,
      }),
  },
  {
    label: t("Zuschlag (%)"),
    item: () =>
      newPricingItem(PricingItemKind.Modifier, {
        kind: PriceModifierKind.Surcharge,
        schema: PriceModifierSchema.Relative,
      }),
  },
  {
    label: t("Zuschlag (€)"),
    item: () =>
      newPricingItem(PricingItemKind.Modifier, {
        kind: PriceModifierKind.Surcharge,
        schema: PriceModifierSchema.Absolute,
      }),
  },
  {
    label: t("Gesamtpreis"),
    item: () => newPricingItem(PricingItemKind.Total),
  },
];

function PricingItemActionsCell({
  item,
  pricing,
  onPatch,
  disabled,
}: {
  item?: PricingItem;
  pricing: Pricing;
  onPatch: (p: PricingPatch) => void;
  disabled?: boolean;
}) {
  const hasTotal = pricing.items.some((i) => i.kind === PricingItemKind.Total);
  const options = addItemOptions.filter(
    (o) => !hasTotal || o.label !== t("Gesamtpreis"),
  );

  return (
    <ETableCell className="w-8 p-0">
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button variant="ghost" className="w-8 rounded-none">
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuGroup>
            <DropdownMenuLabel>{t("Darunter hinzufügen")}</DropdownMenuLabel>
            {options.map((option) => (
              <DropdownMenuItem
                key={option.label}
                onSelect={() =>
                  onPatch({
                    items: addPricingItemBelow(
                      pricing.items,
                      item?.order ?? 0,
                      option.item(),
                    ),
                  })
                }
              >
                <Plus />
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
          {item && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() =>
                  onPatch({
                    items: removePricingItem(pricing.items, item.order),
                  })
                }
              >
                <Trash2 />
                {t("Löschen")}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </ETableCell>
  );
}

function DynamicPricingItems({
  pricing,
  onPatch,
  disabled,
  readOnly,
}: {
  pricing: Pricing;
  onPatch: (p: PricingPatch) => void;
  disabled?: boolean;
  readOnly?: boolean;
}) {
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const dynamicItems = moveDynamicItem(
      pricing.dynamicItems,
      Number(active.id),
      Number(over.id),
    );
    if (dynamicItems !== pricing.dynamicItems) {
      onPatch({ dynamicItems });
    }
  };

  return (
    <DragNDropWrapper
      items={pricing.dynamicItems.map((i) => i.order)}
      handleDragEnd={handleDragEnd}
      disabled={disabled || readOnly}
    >
      <ETable>
        <ETableHeader>
          <ETableRow>
            <ETableHead />
            <ETableHead className="w-full">{t("Name")}</ETableHead>
            <ETableHead>{t("Einheit")}</ETableHead>
            <ETableHead>{t("Preis / Einheit")}</ETableHead>
            <ETableHead />
          </ETableRow>
        </ETableHeader>
        <ETableBody>
          {pricing.dynamicItems.length === 0 && (
            // without items, the actions menu adds the first one
            <ETableRow>
              <ETableCell aria-hidden="true" colSpan={4} />
              <DynamicPricingItemActionsCell
                pricing={pricing}
                onPatch={onPatch}
                disabled={disabled || readOnly}
              />
            </ETableRow>
          )}
          {pricing.dynamicItems.map((item) => (
            <DynamicPricingItemRow
              key={item.order}
              item={item}
              pricing={pricing}
              onPatch={onPatch}
              disabled={disabled}
              readOnly={readOnly}
            />
          ))}
        </ETableBody>
      </ETable>
    </DragNDropWrapper>
  );
}

function DynamicPricingItemRow({
  item,
  pricing,
  onPatch,
  disabled,
  readOnly,
}: {
  item: DynamicItem;
  pricing: Pricing;
  onPatch: (p: PricingPatch) => void;
  disabled?: boolean;
  readOnly?: boolean;
}) {
  switch (item.kind) {
    case PositionKind.Text:
      return (
        <ETableRow>
          <ETableCellDragNDropGrip
            id={item.order}
            disabled={disabled || readOnly}
          />
          <ETableCellInput
            value={item.text}
            onChange={(e) =>
              onPatch({
                dynamicItems: updateDynamicItem(
                  pricing.dynamicItems,
                  item.order,
                  { ...item, text: e.target.value },
                ),
              })
            }
            className={cn(item.parentIdx !== 0 && "pl-6")}
            fieldName={`dynamicItems.${item.order}.text`}
            colSpan={3}
            readOnly={readOnly}
            disabled={disabled}
          />
          <DynamicPricingItemActionsCell
            item={item}
            pricing={pricing}
            onPatch={onPatch}
            disabled={disabled || readOnly}
          />
        </ETableRow>
      );
    default:
      return (
        <ETableRow>
          <ETableCellDragNDropGrip
            id={item.order}
            disabled={disabled || readOnly}
          />
          <ETableCellInput
            value={item.text}
            onChange={(e) =>
              onPatch({
                dynamicItems: updateDynamicItem(
                  pricing.dynamicItems,
                  item.order,
                  { ...item, text: e.target.value },
                ),
              })
            }
            className={cn(item.parentIdx !== 0 && "pl-6")}
            fieldName={`dynamicItems.${item.order}.text`}
            readOnly={readOnly}
            disabled={disabled}
          />
          <ETableCellSelect
            label={t("Einheit")}
            items={PositionUnitSelectItems}
            value={item.unit}
            onValueChange={(unit) =>
              onPatch({
                dynamicItems: updateDynamicItem(
                  pricing.dynamicItems,
                  item.order,
                  { ...item, unit: unit as DynamicItem["unit"] },
                ),
              })
            }
            fieldName={`dynamicItems.${item.order}.unit`}
            disabled={disabled || readOnly}
          />
          <ETableCellDecimalInput
            value={item.unitPrice}
            onValueChange={(unitPrice) =>
              onPatch({
                dynamicItems: updateDynamicItem(
                  pricing.dynamicItems,
                  item.order,
                  { ...item, unitPrice },
                ),
              })
            }
            precision={2}
            fieldName={`dynamicItems.${item.order}.unitPrice`}
            readOnly={readOnly}
            disabled={disabled}
          />
          <DynamicPricingItemActionsCell
            item={item}
            pricing={pricing}
            onPatch={onPatch}
            disabled={disabled || readOnly}
          />
        </ETableRow>
      );
  }
}

const addDynamicItemOptions = [
  { label: t("Leistung"), item: () => newDynamicItem(PositionKind.Dynamic) },
  { label: t("Text"), item: () => newDynamicItem(PositionKind.Text) },
];

function DynamicPricingItemActionsCell({
  item,
  pricing,
  onPatch,
  disabled,
}: {
  item?: DynamicItem;
  pricing: Pricing;
  onPatch: (p: PricingPatch) => void;
  disabled?: boolean;
}) {
  return (
    <ETableCell className="w-8 p-0">
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button variant="ghost" className="w-8 rounded-none">
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuGroup>
            <DropdownMenuLabel>{t("Darunter hinzufügen")}</DropdownMenuLabel>
            {addDynamicItemOptions.map((option) => (
              <DropdownMenuItem
                key={option.label}
                onSelect={() =>
                  onPatch({
                    dynamicItems: addDynamicItemBelow(
                      pricing.dynamicItems,
                      item?.order ?? 0,
                      option.item(),
                    ),
                  })
                }
              >
                <Plus />
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
          {item && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() =>
                  onPatch({
                    dynamicItems: removeDynamicItem(
                      pricing.dynamicItems,
                      item.order,
                    ),
                  })
                }
              >
                <Trash2 />
                {t("Löschen")}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </ETableCell>
  );
}
